import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import { APPLICATION_REDUCER } from '../../store/reducers/application.reducer';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationService } from './application.service';
import { QUERY_PARAMS } from '../configurations/router';

@Injectable({
  providedIn: 'root',
})
export class QueryNavigationService {
  readonly ids$: Observable<string[]> = this.applicationService.queryDataIds$;
  private navigationState: number = 1;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly applicationService: ApplicationService,
  ) {}

  get currentNavState(): number {
    return this.navigationState;
  }

  get isMarkCompleteActive$(): Observable<boolean> {
    return this.activatedRoute.queryParams.pipe(map((params) => params[QUERY_PARAMS.markComplete] === 'true'));
  }

  async navigate(direction: TQueryNavigationDirection): Promise<void> {
    const { queryName } = await firstValueFrom(this.store.select(APPLICATION_REDUCER.selectDataQueryParam));
    const currentIds = await firstValueFrom(this.ids$);
    const updatedOrderIds = this.getUpdatedOrderIds(direction, currentIds);

    await this.router.navigate([''], {
      queryParams: { [queryName]: updatedOrderIds.join(',') },
      queryParamsHandling: 'merge',
    });

    const afterNavigationIds = await firstValueFrom(this.ids$);
    this.updateNavigationState(direction, afterNavigationIds);
  }

  private updateNavigationState(direction: TQueryNavigationDirection, ids: string[]): void {
    switch (direction) {
      case 'forward':
        this.navigationState = this.navigationState + 1 > ids.length ? 1 : this.navigationState + 1;
        break;
      case 'back':
        this.navigationState = this.navigationState - 1 <= 0 ? ids.length : this.navigationState - 1;
        break;
      case 'skip':
        this.navigationState = this.navigationState > ids.length ? ids.length : this.navigationState;
    }
  }

  private getUpdatedOrderIds(direction: TQueryNavigationDirection, ids: string[]): string[] {
    const lastElementPosition = -1;

    switch (direction) {
      case 'forward':
        return [...ids.slice(1), ids[0]];
      case 'back':
        return [...ids.slice(lastElementPosition), ...ids.slice(0, lastElementPosition)];
      case 'skip':
        return ids.slice(1);
    }
  }
}

export type TQueryNavigationDirection = 'forward' | 'back' | 'skip';
